import { EdgelessFrameManagerIdentifier } from '@blocksuite/affine-block-frame';
import { EdgelessCRUDExtension } from '@blocksuite/affine-block-surface';
import { MindmapStyle, SurfaceRefBlockSchema } from '@blocksuite/affine-model';
import {
  type SlashMenuActionItem,
  type SlashMenuConfig,
  SlashMenuConfigExtension,
  type SlashMenuItem,
  type SlashMenuSubMenu,
} from '@blocksuite/affine-widget-slash-menu';
import { Bound } from '@blocksuite/global/gfx';
import {
  EdgelessIcon,
  FrameIcon,
  GroupingIcon,
  MindmapIcon,
} from '@blocksuite/icons/lit';
import {
  getBlocksuiteLocaleKey,
  LocaleProvider,
  translateBlocksuiteText,
  type LocaleService,
} from '@blocksuite/affine-shared/services';
import { BlockSelection } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';

import { insertSurfaceRefBlockCommand } from '../commands';
import {
  buildSurfaceReferenceLabels,
  type SurfaceRefReferenceLabel,
} from './group-reference-label';
import {
  createEdgelessTooltip,
  createFrameTooltip,
  createMindMapTooltip,
} from './tooltips';

type SurfaceReferenceMenuOptions = {
  frameReferences: readonly SurfaceRefReferenceLabel[];
  groupReferences: readonly SurfaceRefReferenceLabel[];
  insertReference: (reference: string) => void;
  group: NonNullable<SlashMenuSubMenu['group']>;
  locale?: LocaleService | null;
};

type SurfaceReferenceKind = 'frame' | 'group';

function getReferenceTypePrefix(
  kind: SurfaceReferenceKind,
  locale: LocaleService | null | undefined
) {
  const fallback = kind === 'frame' ? 'Frame' : 'Group';
  return translateBlocksuiteText(
    locale,
    getBlocksuiteLocaleKey('slash.item', fallback),
    fallback
  );
}

function isDefaultReferenceTitle(
  reference: SurfaceRefReferenceLabel,
  kind: SurfaceReferenceKind
) {
  const defaultPrefix = kind === 'frame' ? 'Frame' : 'Group';
  return new RegExp(`^${defaultPrefix} (\\d+)(?: (\\(\\d+\\)))?$`, 'u').test(
    reference.title
  );
}

function getReferenceItemBaseName(
  reference: SurfaceRefReferenceLabel,
  kind: SurfaceReferenceKind,
  locale: LocaleService | null | undefined
) {
  if (isDefaultReferenceTitle(reference, kind) || !reference.title.trim()) {
    return translateSlashText(
      locale,
      'item',
      kind === 'frame' ? 'Reference frames' : 'Reference groups'
    );
  }

  return `${getReferenceTypePrefix(kind, locale)} ${reference.title}`;
}

function buildReferenceItemNames(
  references: readonly SurfaceRefReferenceLabel[],
  kind: SurfaceReferenceKind,
  locale: LocaleService | null | undefined
) {
  const baseNames = references.map(reference =>
    getReferenceItemBaseName(reference, kind, locale)
  );
  const nameCounts = new Map<string, number>();
  const nameOrdinals = new Map<string, number>();

  for (const name of baseNames) {
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  }

  return baseNames.map((name, index) => {
    const ordinal = (nameOrdinals.get(name) ?? 0) + 1;
    nameOrdinals.set(name, ordinal);

    return isDefaultReferenceTitle(references[index], kind) ||
      !references[index].title.trim() ||
      (nameCounts.get(name) ?? 0) > 1
      ? `${name} ${ordinal}`
      : name;
  });
}

function translateSlashText(
  locale: LocaleService | null | undefined,
  kind: 'item' | 'description' | 'tooltip',
  fallback: string
) {
  return translateBlocksuiteText(
    locale,
    getBlocksuiteLocaleKey(`slash.${kind}`, fallback),
    fallback
  );
}

export function buildSurfaceReferenceMenu({
  frameReferences,
  groupReferences,
  insertReference,
  group,
  locale,
}: SurfaceReferenceMenuOptions): SlashMenuSubMenu | null {
  const frameNames = buildReferenceItemNames(frameReferences, 'frame', locale);
  const frameItems = frameReferences.map<SlashMenuActionItem>(
    (frame, index) => ({
      name: frameNames[index],
      icon: FrameIcon(),
      tooltip: {
        figure: createEdgelessTooltip(getReferenceTypePrefix('frame', locale)),
        caption: translateSlashText(locale, 'tooltip', 'Edgeless'),
      },
      action: () => insertReference(frame.id),
    })
  );

  const groupNames = buildReferenceItemNames(groupReferences, 'group', locale);
  const groupItems = groupReferences.map<SlashMenuActionItem>(
    (reference, index) => ({
      name: groupNames[index],
      icon: GroupingIcon(),
      tooltip: {
        figure: createEdgelessTooltip(getReferenceTypePrefix('group', locale)),
        caption: translateSlashText(locale, 'tooltip', 'Edgeless'),
      },
      action: () => insertReference(reference.id),
    })
  );

  const referenceSubMenus: SlashMenuSubMenu[] = [];
  if (frameItems.length > 0) {
    referenceSubMenus.push({
      name: translateSlashText(locale, 'item', 'Reference frames'),
      description: translateSlashText(
        locale,
        'description',
        'Choose a frame from this canvas.'
      ),
      icon: FrameIcon(),
      subMenu: frameItems,
    });
  }
  if (groupItems.length > 0) {
    referenceSubMenus.push({
      name: translateSlashText(locale, 'item', 'Reference groups'),
      description: translateSlashText(
        locale,
        'description',
        'Choose a group from this canvas.'
      ),
      icon: GroupingIcon(),
      subMenu: groupItems,
    });
  }

  if (referenceSubMenus.length === 0) return null;

  return {
    name: translateSlashText(locale, 'item', 'Reference canvas element'),
    description: translateSlashText(
      locale,
      'description',
      'Insert a reference to an existing frame or group.'
    ),
    icon: EdgelessIcon(),
    searchAlias: ['reference', 'canvas', 'edgeless'],
    group,
    subMenu: referenceSubMenus,
  };
}

const surfaceRefSlashMenuConfig: SlashMenuConfig = {
  items: ({ std, model }) => {
    const crud = std.get(EdgelessCRUDExtension);
    const frameMgr = std.get(EdgelessFrameManagerIdentifier);
    const locale = std.getOptional(LocaleProvider);
    const translate = (
      kind: 'item' | 'description' | 'tooltip',
      fallback: string
    ) => translateSlashText(locale, kind, fallback);

    const findSpace = (bound: Bound, padding = 20) => {
      const gfx = std.get(GfxControllerIdentifier);
      let elementInFrameBound = gfx.grid.search(bound);
      while (elementInFrameBound.length > 0) {
        const rightElement = elementInFrameBound.reduce((a, b) => {
          return a.x + a.w > b.x + b.w ? a : b;
        });
        bound.x = rightElement.x + rightElement.w + padding;
        elementInFrameBound = gfx.grid.search(bound);
      }
      return bound;
    };

    const insertSurfaceRefAndSelect = (reference: string) => {
      const [_, result] = std.command.exec(insertSurfaceRefBlockCommand, {
        reference,
        place: 'after',
        removeEmptyLine: true,
        selectedModels: [model],
      });
      if (!result.insertedSurfaceRefBlockId) return;

      std.selection.set([
        std.selection.create(BlockSelection, {
          blockId: result.insertedSurfaceRefBlockId,
        }),
      ]);
    };

    let index = 0;

    const insertBlankFrameItem: SlashMenuItem = {
      name: translate('item', 'Frame'),
      description: translate('description', 'Insert a blank frame'),
      icon: FrameIcon(),
      tooltip: {
        figure: createFrameTooltip(
          translate('tooltip', 'Create a blank frame in Edgeless'),
          translate('item', 'Frame')
        ),
        caption: translate('tooltip', 'Frame'),
      },
      group: `5_Edgeless Element@${index++}`,
      action: () => {
        const frameBound = findSpace(Bound.fromXYWH([0, 0, 1600, 900]));
        const frame = frameMgr.createFrameOnBound(frameBound);
        insertSurfaceRefAndSelect(frame.id);
      },
    };

    const insertMindMapItem: SlashMenuItem = {
      name: translate('item', 'Mind Map'),
      description: translate('description', 'Insert a mind map'),
      icon: MindmapIcon(),
      tooltip: {
        figure: createMindMapTooltip(
          translate('tooltip', 'Create a mind map in Edgeless'),
          translate('item', 'Mind Map')
        ),
        caption: translate('tooltip', 'Edgeless'),
      },
      group: `5_Edgeless Element@${index++}`,
      action: () => {
        const bound = findSpace(Bound.fromXYWH([0, 0, 200, 200]), 150);
        const { x, y, h } = bound;

        const rootW = 145;
        const rootH = 50;

        const nodeW = 80;
        const nodeH = 35;

        const centerVertical = y + h / 2;
        const rootX = x;
        const rootY = centerVertical - rootH / 2;

        type MindMapNode = {
          children: MindMapNode[];
          text: string;
          xywh: string;
        };

        const root: MindMapNode = {
          children: [],
          text: 'Mind Map',
          xywh: `[${rootX},${rootY},${rootW},${rootH}]`,
        };

        for (let i = 0; i < 3; i++) {
          const nodeX = x + rootW + 300;
          const nodeY = centerVertical - nodeH / 2 + (i - 1) * 50;
          root.children.push({
            children: [],
            text: 'Text',
            xywh: `[${nodeX},${nodeY},${nodeW},${nodeH}]`,
          });
        }

        const mindmapId = crud.addElement('mindmap', {
          style: MindmapStyle.ONE,
          children: root,
        });
        if (!mindmapId) return;

        insertSurfaceRefAndSelect(mindmapId);
      },
    };

    const frameReferences = buildSurfaceReferenceLabels(
      frameMgr.frames.map(frameModel => ({
        id: frameModel.id,
        title: frameModel.props.title.toString(),
      }))
    );
    const groupReferences = buildSurfaceReferenceLabels(
      crud.getElementsByType('group').map(group => ({
        id: group.id,
        title: group.title.toString(),
      }))
    );

    const referenceMenu = buildSurfaceReferenceMenu({
      frameReferences,
      groupReferences,
      insertReference: insertSurfaceRefAndSelect,
      group: `5_Edgeless Element@${index++}`,
      locale,
    });

    return [
      insertBlankFrameItem,
      insertMindMapItem,
      ...(referenceMenu ? [referenceMenu] : []),
    ];
  },
};

export const SurfaceRefSlashMenuConfigExtension = SlashMenuConfigExtension(
  SurfaceRefBlockSchema.model.flavour,
  surfaceRefSlashMenuConfig
);
